import { APP_POOL_CONFIG } from '@synergy/config';
import { ArrayHelpers, FieldArray, FormikProps, useFormikContext } from 'formik';
import { Box, Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Locale } from '@synergy/models';
import { PaloNotice } from '@synergy/components-palo';
import { ReactElement, cloneElement, useEffect, useRef, useState } from 'react';

import { CustomData } from '../../../models/custom-data';
import { CustomPosition } from '../../../../../../common/components/free-position-dialog/model/custom-position';
import { CustomPositionFields } from '../../../../../../common/components/free-position-dialog/model/custom-position-fields';
import { DetailPositionsTable } from './detail-positions-table';
import { FreePositionDialog } from '../../../../../../common/components/free-position-dialog/free-position-dialog';
import { GenericItemModal } from './generic-item-modal';
import { InvoiceGenericItem, InvoiceGenericItemType } from '../../../../models/invoice-generic-item';
import {
    InvoiceItem,
    isInvoiceGenericItem,
    isInvoiceProductItem,
    isInvoiceTextItem,
} from '../../../../models/invoice-item';
import { InvoiceProductItem } from '../../../../models/invoice-product-item';
import { InvoiceTextItem } from '../../../../models/invoice-text-item';
import { PositionEditModal } from './position-edit-modal';
import { PositionsFormValues, invoiceItemFieldNames, positionFieldNames } from '../../../models/positions-form-values';
import { Product } from '../../../../../../common/models/product';
import { ProductSnapshot } from '../../../../../../common/models/product-snapshot';
import { TaxRate } from '../../../../../../common/models/tax-rate';
import { TaxRateOverrideModal } from './tax-rate-override-modal';
import { TextItemModal } from './text-item-modal';
import { getDuplicatedProductItem } from '../utils/get-duplicated-product-item';
import { getHighestId } from '../utils/get-highest-id';
import { getLastPosition } from '../utils/get-last-position';
import { invoiceDetailOverviewDataSelector } from '../../../store/overview/invoice-detail-overview-selectors';
import { isCreditSelector } from '../../../store/positions/invoice-detail-positions-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useGoodsCounterAccounts } from './use-goods-counter-accounts';
import { useInvoiceDetailContext } from '../../../../context/invoice-detail-context';

interface DetailPositionsTableProps {
    currency: string;
    locale: Locale;
    appLocale: Locale;
    taxRates: TaxRate[];
    /** Sits above the table: adding a row, adding several. */
    toolbar?: ReactElement;
}

export type ItemsArrayHelper = ArrayHelpers & {
    form: FormikProps<PositionsFormValues>;
    name: string;
};

export const DetailPositionsTableContainer = (props: DetailPositionsTableProps) => {
    const { locale, appLocale, currency, taxRates, toolbar } = props;
    const [textItemIndex, setTextItemIndex] = useState<number | undefined>(undefined);
    const [textItem, setTextItem] = useState<InvoiceTextItem | undefined>(undefined);
    const [showEditTextItemModal, setShowEditTextItemModal] = useState(false);
    const [genericItemIndex, setGenericItemIndex] = useState<number | undefined>(undefined);
    const [genericItem, setGenericItem] = useState<InvoiceGenericItem | undefined>(undefined);
    const [customPosition, setCustomPosition] = useState<CustomPosition | undefined>(undefined);
    const [customData, setCustomData] = useState<CustomData | undefined>(undefined);
    const isCredit = useAppSelector(isCreditSelector);
    const overviewValues = useAppSelector(invoiceDetailOverviewDataSelector).values;
    const savedSupplier = overviewValues.supplierAccount;
    const savedSupplierId = savedSupplier?.id;
    const formikContext = useFormikContext<PositionsFormValues>();
    const helperRef = useRef<ItemsArrayHelper | null>(null);

    const {
        supplierAccountId,
        supplierAccountName,
        invoiceCountryCode,
        showAddPositionModal,
        setShowAddPositionModal,
        setIsNewTextItem,
        showNewTextItemModal,
        setShowNewTextItemModal,
        selectedProduct,
        setSelectedProduct,
        setInvoiceItems,
        taxRateForOverride,
        setTaxRateForOverride,
        showTaxRateOverrideModal,
        setShowEditPositionModal,
        showEditPositionModal,
        setShowGenericItemModal,
        showGenericItemModal,
    } = useInvoiceDetailContext();

    // The head's field while it is on screen, the saved document otherwise.
    const country = undefined !== invoiceCountryCode ? invoiceCountryCode : overviewValues.invoiceAddress?.countryCode;
    const goodsCounterAccounts = useGoodsCounterAccounts(country);
    const intl = useIntl();

    /*
     * Lines already on the document keep their account when the address moves
     * to another country — on purpose, someone may have chosen it. They are not
     * left unsaid, though: the editor offers to book them the new way (O-12).
     */
    const previousCountry = useRef(country);
    const [countryChanged, setCountryChanged] = useState(false);

    useEffect(() => {
        // Clearing the recipient empties the country on the way to the next
        // one; the comparison is with the last country there was.
        if (!country) {
            return;
        }

        if (previousCountry.current && previousCountry.current !== country) {
            setCountryChanged(true);
        }
        previousCountry.current = country;
    }, [country]);

    /**
     * The supplier the head holds right now, and only where the head is not on
     * screen the one the document was saved with. Reading the saved one while
     * the field says something else let articles of another supplier onto the
     * document (Prüfbericht 28.09.2026, T2/T6).
     */
    const supplierId = undefined !== supplierAccountId ? supplierAccountId ?? undefined : savedSupplierId;
    const supplierName = undefined !== supplierAccountId ? supplierAccountName : savedSupplier?.name;

    useEffect(() => {
        if (selectedProduct && helperRef.current) {
            handleProductSelected(helperRef.current)(selectedProduct);
            setSelectedProduct?.(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProduct]);

    useEffect(() => {
        setTextItemIndex(formikContext.values.allItems.filter(isInvoiceTextItem).length);
        setTextItem({ id: -1, position: Number.POSITIVE_INFINITY, title: '', description: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showNewTextItemModal]);

    useEffect(() => {
        if (helperRef.current) {
            setInvoiceItems?.(
                helperRef.current.form.values.allItems.filter(
                    item => isInvoiceProductItem(item) || isInvoiceGenericItem(item)
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [helperRef.current?.form.values.allItems]);

    /**
     * The account an article is booked to, by the country of the invoice address
     * and, where that country says so, the article's own rate (GF 16.10.2026,
     * #2263). The account brings its rate: a German member pays 19 %, whatever
     * the article carries here. Without a default the article keeps its rate and
     * the account is picked by hand.
     */
    const bookingFor = (tax?: number | null): { taxRate?: TaxRate; tax?: number } => {
        const taxRateId =
            (null !== tax && undefined !== tax
                ? goodsCounterAccounts?.taxRateIdsByArticleRate[String(tax)]
                : undefined) ?? goodsCounterAccounts?.defaultTaxRateId;
        const taxRate = taxRates.find(candidate => candidate.id === taxRateId);

        return taxRate ? { taxRate, tax: taxRate.taxPercentage ?? undefined } : { tax: tax ?? undefined };
    };

    /**
     * A row that books to an account meant for another country than the one of
     * the invoice address — left so on purpose with "So lassen", or saved
     * before the address changed. Marked for as long as it holds (R-175).
     */
    const isOffCountry = (item: InvoiceProductItem): boolean => {
        const expected = goodsCounterAccounts ? bookingFor(item.tax).taxRate : undefined;

        return undefined !== expected && !!item.taxRate && item.taxRate.id !== expected.id;
    };

    const getProductItem = (productSnapshot: ProductSnapshot, helper: ItemsArrayHelper) => {
        const product = InvoiceProductItem.fromPlainObject({
            ...productSnapshot,
            id: getHighestId(helper) + 1,
            position: helper.form.values.allItems.length === 0 ? 0 : getLastPosition(helper) + 1,
            price: isCredit ? (productSnapshot.price ?? 0) * -1 : productSnapshot.price,
            ...bookingFor(productSnapshot.tax),
        });

        return product;
    };

    const handleProductSelected = (helper: ItemsArrayHelper) => (selectedProduct: Product | null) => {
        if (selectedProduct) {
            const productSnapshot = InvoiceProductItem.fromProduct(
                selectedProduct,
                locale,
                currency,
                APP_POOL_CONFIG.address.country
            );

            const product = getProductItem(productSnapshot, helper);
            helper.push(product);
        }
    };

    /**
     * A row that arrives empty and waits for its article.
     *
     * It is ERPNext's "Add row": the row is there first, and what it is about
     * is searched for in the row itself. The free position keeps its own
     * dialog, because it has no article to find (#2208).
     */
    const addBlankRow = (helper: ItemsArrayHelper, at?: number) => {
        const items = itemsOf(helper);
        items.splice(
            undefined === at ? items.length : at,
            0,
            InvoiceProductItem.fromPlainObject({
                id: getHighestId(helper) + 1,
                name: {},
                quantity: 1,
                price: 0,
                discount: 0,
                orderUnit: '',
                isBestPrice: false,
                currency,
                // The account of the address waits in the row before its
                // article does (#2263).
                ...bookingFor(),
            })
        );
        setItems(helper, items);
    };

    /**
     * Writes the new order in one go.
     *
     * A position is numbered by its place, and the table sorts by that number.
     * Moving a row with Formik's own helper and renumbering afterwards reads
     * the array as it was before the move, so the numbers land on the old order
     * and the sort puts every row back where it came from — the move looked as
     * if nothing had happened (#2208).
     */
    const setItems = (helper: ItemsArrayHelper, items: InvoiceItem[]) => {
        items.forEach((item, index) => {
            item.position = index;
        });

        helper.form.setFieldValue(positionFieldNames.allItems, items);
    };

    const itemsOf = (helper: ItemsArrayHelper) => [...(helper.form.values.allItems as InvoiceItem[])];

    const handleShowAddPositionModal = (index?: number, customPosition?: InvoiceProductItem) => {
        setShowAddPositionModal?.(true);
        setCustomPosition(customPosition);
        setCustomData(
            customPosition && index !== undefined
                ? {
                      index,
                      discount: customPosition.discount,
                  }
                : undefined
        );
    };

    /**
     * The handles of a row, for the dialog that edits it.
     *
     * The row menu and the dialog offer the same things, so one is not a
     * shorter version of the other; acting closes the dialog, because what it
     * was showing has moved or is gone (#2208).
     */
    const rowHandles = (helper: ItemsArrayHelper, index?: number) => {
        if (undefined === index) {
            return {};
        }

        const andClose = (act: () => void) => () => {
            act();
            handleEditPositionModalClose();
        };

        return {
            onInsertAbove: andClose(() => addBlankRow(helper, index)),
            onInsertBelow: andClose(() => addBlankRow(helper, index + 1)),
            onDuplicate: andClose(() => {
                const items = itemsOf(helper);
                const item = items[index];

                items.splice(
                    index + 1,
                    0,
                    isInvoiceProductItem(item)
                        ? getDuplicatedProductItem(item, helper)
                        : getDuplicateInvoiceItem(helper, item)
                );
                setItems(helper, items);
            }),
            onDelete: andClose(() => {
                const items = itemsOf(helper);
                items.splice(index, 1);
                setItems(helper, items);
            }),
        };
    };

    const handleShowEditPositionModal = (index?: number, position?: InvoiceProductItem) => {
        setShowEditPositionModal?.(true);
        setCustomData(
            position && index !== undefined
                ? {
                      index,
                  }
                : undefined
        );
    };

    const handleCloseAddPositionModal = () => {
        setShowAddPositionModal?.(false);
        // Reset the prefill/edit state so the next "add free position" (which opens this
        // same dialog directly from the CTA) starts clean. Otherwise a previous edit's
        // customData.index leaks in and the save path does replace() instead of push(),
        // overwriting an existing position, and the dialog opens pre-filled with stale data.
        setCustomPosition(undefined);
        setCustomData(undefined);
    };

    const handleCustomPositionSave = (helper: ItemsArrayHelper) => (value: CustomPosition, data?: unknown) => {
        const customData = data as CustomData;

        const item: InvoiceProductItem = InvoiceProductItem.fromPlainObject({
            ...value,
            discount: customData?.discount || 0,
        });

        if (customData?.index !== undefined) {
            helper.replace(customData.index, item);
        } else {
            item.id = getHighestId(helper) + 1;
            item.position = getLastPosition(helper) + 1;
            helper.push(item);
        }

        handleCloseAddPositionModal();
    };

    const getDuplicateInvoiceItem = (helper: ItemsArrayHelper, item: InvoiceTextItem | InvoiceGenericItem) => {
        return {
            ...item,
            id: getHighestId(helper) + 1,
            position: getLastPosition(helper) + 1,
        };
    };

    const handleTaxRateSelected =
        (helper: ItemsArrayHelper) =>
        (index: number, value: TaxRate | null): void => {
            const prefix = `${positionFieldNames.allItems}.${index}`;

            helper.form.setFieldValue(`${prefix}.taxRate`, value);
            helper.form.setFieldValue(`${prefix}.tax`, value?.taxPercentage ?? null);
        };

    const handleCloseAddTextItemModal = () => {
        setShowNewTextItemModal?.(false);
        setShowEditTextItemModal(false);
        setIsNewTextItem?.(false);
        setTextItemIndex(undefined);
    };

    const handleEditPositionModalClose = () => {
        setShowEditPositionModal?.(false);
        setCustomData(undefined);
    };

    const handleCloseGenericItemModal = () => {
        setGenericItem(undefined);
        setGenericItemIndex(undefined);
        setShowGenericItemModal?.({ show: false, type: InvoiceGenericItemType.Surcharge });
    };

    return (
        <FieldArray name={positionFieldNames.allItems}>
            {(helper: ItemsArrayHelper) => {
                helperRef.current = helper;

                const offLines =
                    countryChanged && goodsCounterAccounts
                        ? (helper.form.values.allItems as InvoiceItem[]).filter(
                              item =>
                                  isInvoiceProductItem(item) &&
                                  undefined !== bookingFor(item.tax).taxRate &&
                                  item.taxRate?.id !== bookingFor(item.tax).taxRate?.id
                          )
                        : [];

                return (
                    <>
                        {offLines.length > 0 && (
                            <Box sx={{ mb: '8px' }}>
                                <PaloNotice tone="warn">
                                    <FormattedMessage
                                        id="app.invoices.positions.countryChanged"
                                        values={{ count: offLines.length }}
                                    />{' '}
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => {
                                            setItems(
                                                helper,
                                                itemsOf(helper).map(item =>
                                                    isInvoiceProductItem(item)
                                                        ? InvoiceProductItem.fromPlainObject({
                                                              ...item,
                                                              ...bookingFor(item.tax),
                                                          })
                                                        : item
                                                )
                                            );
                                            setCountryChanged(false);
                                        }}
                                    >
                                        <FormattedMessage id="app.invoices.positions.rebookAll" />
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="inherit"
                                        onClick={() => setCountryChanged(false)}
                                    >
                                        <FormattedMessage id="app.invoices.positions.keepAccounts" />
                                    </Button>
                                </PaloNotice>
                            </Box>
                        )}
                        <DetailPositionsTable
                            currency={currency}
                            taxRates={taxRates}
                            toolbar={
                                toolbar &&
                                cloneElement(toolbar as ReactElement<{ onAddRow?: () => void }>, {
                                    onAddRow: () => addBlankRow(helper),
                                })
                            }
                            onDeleteMany={indices => {
                                // From the back, so the places of the ones still
                                // to go do not shift under the loop.
                                const items = itemsOf(helper);
                                [...indices]
                                    .sort((left, right) => right - left)
                                    .forEach(index => items.splice(index, 1));
                                setItems(helper, items);
                            }}
                            onDuplicateMany={indices => {
                                const items = itemsOf(helper);
                                [...indices]
                                    .sort((left, right) => right - left)
                                    .forEach(index => {
                                        const item = items[index];

                                        if (isInvoiceProductItem(item)) {
                                            items.splice(index + 1, 0, getDuplicatedProductItem(item, helper));
                                        } else {
                                            items.splice(index + 1, 0, getDuplicateInvoiceItem(helper, item));
                                        }
                                    });
                                setItems(helper, items);
                            }}
                            accountHintOf={item => {
                                // The invoice is refused while the tax is not the
                                // account's, so the line says so first (#2326 R-246).
                                const accountRate = item.taxRate?.taxPercentage;

                                if (null != accountRate && accountRate !== (item.tax ?? 0)) {
                                    return intl.formatMessage(
                                        { id: 'app.invoices.positions.taxNotForAccount' },
                                        { rate: accountRate }
                                    );
                                }

                                return isOffCountry(item)
                                    ? intl.formatMessage({ id: 'app.invoices.positions.accountNotForCountry' })
                                    : undefined;
                            }}
                            supplierIds={supplierId ? [supplierId] : undefined}
                            supplierName={supplierName}
                            supplierMissing={!supplierId}
                            onPickProduct={(index, selectedProduct) => {
                                // The blank row keeps its place and its number;
                                // only what it is about arrives (#2208).
                                const snapshot = InvoiceProductItem.fromProduct(
                                    selectedProduct,
                                    locale,
                                    currency,
                                    APP_POOL_CONFIG.address.country
                                );
                                const blank = (helper.form.values.allItems as InvoiceItem[])[index];
                                helper.replace(
                                    index,
                                    InvoiceProductItem.fromPlainObject({
                                        ...snapshot,
                                        id: blank.id,
                                        position: blank.position,
                                        price: isCredit ? (snapshot.price ?? 0) * -1 : snapshot.price,
                                        ...bookingFor(snapshot.tax),
                                    })
                                );
                            }}
                            onMove={(from, to) => {
                                const items = itemsOf(helper);
                                const [moved] = items.splice(from, 1);
                                items.splice(to, 0, moved);
                                setItems(helper, items);
                            }}
                            onDelete={index => {
                                helper.remove(index);
                                (helper.form.values.allItems as InvoiceItem[]).forEach((item, itemIndex) => {
                                    item.position = itemIndex;
                                });
                            }}
                            onEditProductItem={handleShowAddPositionModal}
                            onEditProductProductItem={handleShowEditPositionModal}
                            onInsert={(index, where) => addBlankRow(helper, 'above' === where ? index : index + 1)}
                            onDuplicateProductItem={(productItem, index) => {
                                // Right under the row it copies, the way ERPNext
                                // does it — appended at the end the copy was lost
                                // in a long document.
                                const items = itemsOf(helper);
                                items.splice(index + 1, 0, getDuplicatedProductItem(productItem, helper));
                                setItems(helper, items);
                            }}
                            onEditTextItem={(index, item) => {
                                setShowEditTextItemModal(true);
                                setTextItemIndex(index);
                                setTextItem(item);
                            }}
                            onDuplicateTextItem={item => helper.push(getDuplicateInvoiceItem(helper, item))}
                            onTaxRateSelected={handleTaxRateSelected(helper)}
                            onEditGenericItem={(index, item) => {
                                setShowGenericItemModal?.({ show: true, type: item.type });
                                setGenericItemIndex(index);
                                setGenericItem(item);
                            }}
                            onDuplicateGenericItem={item => helper.push(getDuplicateInvoiceItem(helper, item))}
                        />

                        <FreePositionDialog
                            customPosition={customPosition}
                            customData={customData}
                            isOpen={showAddPositionModal}
                            currencySymbol={currency}
                            locale={appLocale}
                            requiredFields={[
                                CustomPositionFields.OrderUnit,
                                CustomPositionFields.Price,
                                CustomPositionFields.Tax,
                                CustomPositionFields.Quantity,
                            ]}
                            onCancel={handleCloseAddPositionModal}
                            onSave={handleCustomPositionSave(helper)}
                        />

                        <TextItemModal
                            showTextItemModal={showNewTextItemModal || showEditTextItemModal}
                            onModalClose={handleCloseAddTextItemModal}
                            helper={helper}
                            textItemIndex={textItemIndex}
                            textItem={textItem}
                            onTextItemChange={setTextItem}
                        />

                        <TaxRateOverrideModal />

                        <PositionEditModal
                            showPositionEditModal={showEditPositionModal}
                            positionIndex={customData?.index}
                            currency={currency}
                            taxRates={taxRates}
                            onTaxRateSelected={handleTaxRateSelected(helper)}
                            onModalClose={handleEditPositionModalClose}
                            {...rowHandles(helper, customData?.index)}
                        />

                        <GenericItemModal
                            helper={helper}
                            genericItem={genericItem}
                            genericItemIndex={genericItemIndex}
                            genericItemType={showGenericItemModal.type}
                            showGenericItemModal={showGenericItemModal.show}
                            onModalClose={handleCloseGenericItemModal}
                            taxRates={taxRates}
                        />
                    </>
                );
            }}
        </FieldArray>
    );
};
